import { useRef, useState } from "react";
import { ImagePlus, X, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MAX_BYTES = 25 * 1024 * 1024; // matches backend MAX_UPLOAD_SIZE

// Attach button (beside the Live comment input) + pending-attachment chip
// with upload progress, retry and remove. Uses the existing /uploads
// endpoint (same storage, size limit, content rules and moderation scan).
export function useChatAttachment() {
  const [att, setAtt] = useState(null); // { file, localUrl, type, progress, status: uploading|ready|error, url }
  const fileRef = useRef(null);

  const upload = async (file, localUrl, type) => {
    setAtt({ file, localUrl, type, progress: 0, status: "uploading", url: null });
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setAtt((a) => (a ? { ...a, progress: Math.min(99, Math.round((e.loaded / e.total) * 100)) } : a));
        },
      });
      setAtt((a) => (a ? { ...a, progress: 100, status: "ready", url: data.url } : a));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed — tap retry.");
      setAtt((a) => (a ? { ...a, status: "error" } : a));
    }
  };

  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const type = file.type.startsWith("video/") ? "video" : file.type.startsWith("image/") || /\.(heic|heif)$/i.test(file.name) ? "image" : null;
    if (!type) { toast.error("Pictures and videos only."); return; }
    if (file.size > MAX_BYTES) { toast.error("File too large (max 25MB)."); return; }
    upload(file, URL.createObjectURL(file), type);
  };

  const clear = () => setAtt((a) => { if (a?.localUrl) URL.revokeObjectURL(a.localUrl); return null; });
  const retry = () => { if (att?.file) upload(att.file, att.localUrl, att.type); };

  return { att, fileRef, pick, clear, retry, open: () => fileRef.current?.click() };
}

export function ChatAttachButton({ fileRef, onPick, onOpen, disabled }) {
  return (
    <>
      <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm,.heic,.heif" className="hidden" onChange={onPick} data-testid="live-chat-attach-input" />
      <button type="button" onClick={onOpen} disabled={disabled} aria-label="Attach a picture or video" data-testid="live-chat-attach"
        className="shrink-0 h-10 w-10 grid place-items-center rounded-full bg-black/50 hover:bg-black/70 disabled:opacity-40 backdrop-blur-md border border-white/15 text-white">
        <ImagePlus size={16} />
      </button>
    </>
  );
}

export function ChatAttachmentChip({ att, onClear, onRetry }) {
  if (!att) return null;
  return (
    <div data-testid="live-chat-attachment-chip" className="absolute left-2 sm:left-4 z-30 flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/15 rounded-xl p-1.5 pr-2"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 66px)" }}>
      <div className="relative h-12 w-12 rounded-lg overflow-hidden bg-black/40 shrink-0">
        {att.type === "video" ? <video src={att.localUrl} muted playsInline preload="metadata" className="h-full w-full object-cover" /> : <img src={att.localUrl} alt="" className="h-full w-full object-cover" />}
        {att.status === "uploading" && <div className="absolute inset-x-0 bottom-0 h-1 bg-white/20"><div className="h-full bg-rose-500" style={{ width: `${att.progress}%` }} /></div>}
      </div>
      <div className="text-[11px] text-white/85 min-w-[64px]" data-testid="live-chat-attachment-status">
        {att.status === "uploading" && `Uploading ${att.progress}%`}
        {att.status === "ready" && "Ready to send"}
        {att.status === "error" && <span className="text-rose-300">Upload failed</span>}
      </div>
      {att.status === "error" && (
        <button type="button" onClick={onRetry} data-testid="live-chat-attachment-retry" className="h-7 px-2 rounded-full bg-white/15 text-[11px] text-white flex items-center gap-1"><RotateCcw size={12} /> Retry</button>
      )}
      <button type="button" onClick={onClear} aria-label="Remove attachment" data-testid="live-chat-attachment-remove" className="h-7 w-7 grid place-items-center rounded-full bg-white/15 text-white"><X size={12} /></button>
    </div>
  );
}
